import type { FunctionComponent } from 'react';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type { SourceType } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';
import type { PopoverProps, UsePopoverResult } from '@trello/nachos/popover';
import { PopoverScreen } from '@trello/nachos/popover';
import { Spinner } from '@trello/nachos/spinner';
import { useLazyComponent } from '@trello/use-lazy-component';

import { preloadCreateBoardData } from 'app/src/components/CreateBoard/preloadCreateBoardData';
import { usePreSelectWorkspace } from './usePreSelectWorkspace';

export const CreateBoardPopoverScreenId = {
  CreateBoard: 0,
  CreateBoardFromTemplate: 1,
  SelectTemplate: 2,
} as const;

const loadingSpinner = <Spinner centered />;

interface CreateBoardPopoverScreensProps {
  show: UsePopoverResult<HTMLElement>['show'];
  hide: UsePopoverResult<HTMLElement>['hide'];
  push: UsePopoverResult<HTMLElement>['push'];
  isVisible: PopoverProps['isVisible'];
  preSelectedWorkspaceId?: string;
  crossFlowSource?: SourceType;
}

export const CreateBoardPopoverScreens: FunctionComponent<
  CreateBoardPopoverScreensProps
> = (props) => {
  const {
    show,
    hide,
    push,
    isVisible,
    preSelectedWorkspaceId,
    crossFlowSource,
  } = props;

  useEffect(() => {
    preloadCreateBoardData();
    // Only fetch this data when the component first mounts
  }, []);

  usePreSelectWorkspace(preSelectedWorkspaceId);

  const CreateBoardFromTemplatePopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "create-board-from-template-popover" */ '../CreateBoardFromTemplate/CreateBoard'
      ),
    {
      preload: isVisible,
      namedImport: 'CreateBoard',
    },
  );

  const CreateBoardPopoverScreen = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "create-board-popover" */ './CreateBoardContainer'
      ),
    {
      preload: isVisible,
      namedImport: 'CreateBoardContainer',
    },
  );

  const TemplatePickerPopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "template-picker-popover" */ '../CreateBoardFromTemplate/TemplatePickerContainer'
      ),
    {
      preload: isVisible,
      namedImport: 'TemplatePickerContainer',
    },
  );

  const [selectedTemplate, setSelectedTemplate] = useState({
    id: '',
    name: '',
  });

  const handleOnSelectTemplate = useCallback(
    ({ id, name }: { id: string; name: string }) => {
      Analytics.sendUIEvent({
        action: 'clicked',
        actionSubject: 'menuItem',
        actionSubjectId: 'createFromTemplateMenuItem',
        source: 'createFromTemplateInlineDialog',
        containers: {
          board: {
            id,
          },
        },
      });
      setSelectedTemplate({
        id,
        name,
      });
      push(CreateBoardPopoverScreenId.CreateBoardFromTemplate);
    },
    [push],
  );

  const handleOnStartWithTemplate = useCallback(() => {
    push(CreateBoardPopoverScreenId.SelectTemplate);
  }, [push]);

  const showCreateBoardPopover = useCallback(() => {
    show();
    push(CreateBoardPopoverScreenId.CreateBoard);
  }, [show, push]);

  return (
    <>
      <PopoverScreen
        id={CreateBoardPopoverScreenId.CreateBoard}
        title={
          <FormattedMessage
            id="view title.create board"
            defaultMessage="Create board"
            description="Title for create board popover screen"
          />
        }
      >
        <Suspense fallback={loadingSpinner}>
          <CreateBoardPopoverScreen
            onStartWithTemplate={handleOnStartWithTemplate}
            hidePopover={hide}
            showPopover={showCreateBoardPopover}
            crossFlowSource={crossFlowSource}
          />
        </Suspense>
      </PopoverScreen>
      <PopoverScreen
        id={CreateBoardPopoverScreenId.SelectTemplate}
        title={
          <FormattedMessage
            id="view title.create-from-template"
            defaultMessage="Create from template"
            description="Title for create board from template popover screen"
          />
        }
      >
        <Suspense fallback={loadingSpinner}>
          <TemplatePickerPopover
            hide={hide}
            onSelectTemplate={handleOnSelectTemplate}
          />
        </Suspense>
      </PopoverScreen>
      <PopoverScreen
        id={CreateBoardPopoverScreenId.CreateBoardFromTemplate}
        title={selectedTemplate.name}
      >
        <Suspense fallback={loadingSpinner}>
          <CreateBoardFromTemplatePopover
            boardId={selectedTemplate.id}
            hidePopover={hide}
          />
        </Suspense>
      </PopoverScreen>
    </>
  );
};
